// Pre-bakes the character's body and head into offscreen canvases — the 4 body
// frames (emotion-independent) and the 3 per-emotion heads — as SEPARATE
// right-facing squares, so both the Capture preview and the game loop blit
// ready-made layers and share identical pixels.
//
// Body and head are baked separately (not combined) so the renderer can draw the
// cup BETWEEN them: layer order is body (lowest) → cup → head (top). Both body
// and head mirror when the character faces left; the cup never flips (it keeps
// its branding and position), so it stays a runtime drawImage in renderer.ts.
//
// Geometry: each layer is rendered onto a square canvas centered on the unit
// center, sized (in scene units) to a symmetric box big enough that neither
// layer clips. Symmetric-about-center means a horizontal mirror of a square
// reproduces the renderer's per-layer flip exactly.
//
// Seam: "character sprite bake" — see docs/architecture.md seam table.

import type {
  BakedCharacter,
  BakeHeads,
  CharacterVisual,
  Emotion,
  SpriteLayout,
} from "../state/types";

const EMOTIONS: Emotion[] = ["neutral", "happy", "sad"];

// Composite raster resolution (px per baked sprite). The bake represents a
// `sizeSceneUnits`-wide scene region at this fixed pixel size, so memory stays
// ~12 × 512² × 4B ≈ 12.6 MB regardless of scene-unit tuning.
const BAKE_PX = 512;

// The head PNG from the BE compose step is a 512² canvas with the face centered
// to fill it; source the whole image. Default-icon heads (for emotions the
// player didn't capture) are normalized to the same 512² square before baking,
// so this sampling size holds for both.
const HEAD_SRC = 512;

// Half-extent of a layer from the unit center, in scene units. Max over both
// axes so the symmetric square never clips this layer.
function layoutHalfExtent(o: SpriteLayout): number {
  return Math.max(Math.abs(o.offsetX), Math.abs(o.offsetY)) + o.size / 2;
}

/** Bake the 12 head+body composites for one gender. `bodyFrames` must be the
 *  4-frame sequence for the character's gender; `heads` are the per-emotion
 *  head sources — a server-composed PNG for captured emotions, or a normalized
 *  512² default-icon canvas for emotions the player skipped. Returns
 *  right-facing sprites — the renderer mirrors for facing-left. */
export function bakeCharacter(
  heads: BakeHeads,
  bodyFrames: HTMLImageElement[],
  visual: CharacterVisual,
  handImg: CanvasImageSource | null = null,
  defaultHeads: ReadonlySet<Emotion> = new Set(),
): BakedCharacter {
  const useHand = handImg != null && visual.hand != null;
  const handExtent = useHand ? layoutHalfExtent(visual.hand!) : 0;
  // The square must not clip whichever head layout is used (composed or default).
  const headExtent = Math.max(
    layoutHalfExtent(visual.head),
    visual.headDefault ? layoutHalfExtent(visual.headDefault) : 0,
  );
  const half = Math.max(layoutHalfExtent(visual.body), headExtent, handExtent) * 1.05;
  const sizeSceneUnits = half * 2;
  const pxPerUnit = BAKE_PX / sizeSceneUnits;
  const center = BAKE_PX / 2; // canvas center == unit center

  const drawLayer = (
    c2d: CanvasRenderingContext2D,
    img: CanvasImageSource,
    o: SpriteLayout,
    srcSize: number | null,
  ) => {
    const sizePx = o.size * pxPerUnit;
    const dx = center + o.offsetX * pxPerUnit - sizePx / 2;
    const dy = center + o.offsetY * pxPerUnit - sizePx / 2;
    if (srcSize != null) c2d.drawImage(img, 0, 0, srcSize, srcSize, dx, dy, sizePx, sizePx);
    else c2d.drawImage(img, dx, dy, sizePx, sizePx);
  };

  const headFor = (e: Emotion): CanvasImageSource =>
    e === "happy" ? heads.happy : e === "sad" ? heads.sad : heads.neutral;

  const bakeSquare = (draw: (c2d: CanvasRenderingContext2D) => void): HTMLCanvasElement => {
    const canvas = document.createElement("canvas");
    canvas.width = BAKE_PX;
    canvas.height = BAKE_PX;
    draw(canvas.getContext("2d")!);
    return canvas;
  };

  // Body: 4-frame animation, emotion-independent. Each frame its own square.
  const body = bodyFrames.map((bodyImg) =>
    bakeSquare((c2d) => drawLayer(c2d, bodyImg, visual.body, null)),
  );

  // Head: one square per emotion (does not animate per frame). A default-icon
  // head uses visual.headDefault (chibi framing); a composed face uses visual.head.
  const head = {} as Record<Emotion, HTMLCanvasElement>;
  for (const e of EMOTIONS) {
    const layout = defaultHeads.has(e) && visual.headDefault ? visual.headDefault : visual.head;
    head[e] = bakeSquare((c2d) => drawLayer(c2d, headFor(e), layout, HEAD_SRC));
  }

  // Hand (foreground, static): one square, drawn between cup and head at runtime.
  const hand = useHand
    ? bakeSquare((c2d) => drawLayer(c2d, handImg!, visual.hand!, null))
    : null;

  return { body, head, hand, sizeSceneUnits, defaultHeads };
}
